import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Check, Zap, Shield, Clock, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Subscription() {
  const [user, setUser] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    
    if (token && storedUser) {
      setUser(JSON.parse(storedUser));
      fetchSubscriptionStatus(token);
    } else {
      setLoading(false);
    }
  }, []);

  const fetchSubscriptionStatus = async (token) => {
    try {
      const response = await axios.get(`${API}/api/subscription/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptionStatus(response.data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      window.location.href = '/login?redirect=/subscribe';
      return;
    }

    setCheckoutLoading(true);
    try {
      const response = await axios.post(
        `${API}/api/subscription/checkout`,
        {
          package_id: 'monthly',
          origin_url: window.location.origin
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Save session ID for verification after redirect
      localStorage.setItem('checkout_session_id', response.data.session_id);
      
      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Checkout error:', error);
      alert('Error starting checkout. Please try again.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const features = [
    { icon: Zap, title: 'Live Stats Tracking', description: 'Real-time updates during every NBA game' },
    { icon: Shield, title: 'Expert Picks Analysis', description: 'Data-driven insights on player props' },
    { icon: Clock, title: '24/7 Access', description: 'Track your picks anytime, anywhere' }
  ];

  const isSubscribed = subscriptionStatus?.is_active;

  return (
    <>
      <Helmet>
        <title>Subscribe - Live Tracker Pro | PrimeStatLab</title>
        <meta name="description" content="Get access to real-time NBA player prop tracking with Live Tracker Pro subscription." />
      </Helmet>
      
      <div className="min-h-screen bg-background py-12">
        <div className="container max-w-4xl mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-heading font-black mb-4">
              <span className="text-primary">LIVE TRACKER</span> PRO
            </h1>
            <p className="text-xl text-muted-foreground">
              Track your player props in real-time during every NBA game
            </p>
          </div>

          {/* Subscription Card */}
          <Card className="mb-8 border-primary/50">
            <CardHeader className="text-center pb-2">
              <CardTitle className="text-3xl font-heading">Monthly Subscription</CardTitle>
              <div className="mt-4">
                <span className="text-5xl font-black text-primary">$5</span>
                <span className="text-muted-foreground">/month</span>
              </div>
            </CardHeader>
            <CardContent>
              {/* Features List */}
              <div className="grid gap-4 mb-8">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                      <feature.icon className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold">{feature.title}</h3>
                      <p className="text-sm text-muted-foreground">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* What's Included */}
              <div className="bg-muted/50 rounded-lg p-6 mb-8">
                <h3 className="font-semibold mb-4">What's Included:</h3>
                <ul className="space-y-2">
                  {[
                    'Real-time live score tracking during games',
                    'Player stats updated every minute',
                    'See exactly where your prop stands',
                    'Track multiple picks simultaneously',
                    'Mobile-friendly dashboard'
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-primary" />
                      <span className="text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Status & CTA */}
              {loading ? (
                <div className="flex justify-center py-4">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : isSubscribed ? (
                <div className="text-center">
                  <div className="bg-primary/20 text-primary px-4 py-3 rounded-lg mb-4">
                    <p className="font-semibold">You're subscribed!</p>
                    <p className="text-sm">
                      {subscriptionStatus.days_remaining} days remaining
                    </p>
                  </div>
                  <Button asChild className="w-full">
                    <a href="/tracker">Go to Live Tracker</a>
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <Button 
                    onClick={handleSubscribe} 
                    className="w-full text-lg py-6"
                    disabled={checkoutLoading}
                    data-testid="subscribe-btn"
                  >
                    {checkoutLoading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      'Subscribe Now - $5/month'
                    )}
                  </Button>
                  {!user && (
                    <p className="text-center text-sm text-muted-foreground">
                      Don't have an account?{' '}
                      <a href="/register" className="text-primary hover:underline">
                        Sign up first
                      </a>
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* PrizePicks Promo */}
          <Card className="border-yellow-500/50 bg-yellow-500/5">
            <CardContent className="py-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center shrink-0">
                  <span className="text-2xl">🎁</span>
                </div>
                <div>
                  <h3 className="font-semibold text-yellow-500">Get a FREE Month!</h3>
                  <p className="text-sm text-muted-foreground">
                    Sign up for PrizePicks using our link and get your first month of Live Tracker Pro FREE!
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}
